"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Minimize2, Maximize2, Coffee } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getBreakState,
  setBreakState,
  clearBreakState,
  BREAK_EVENT,
  type ActiveBreakState,
} from "@/lib/chat/break-state";
import { MY_AVAILABILITY_EVENT, emitMyAvailability } from "@/lib/chat/my-availability";

// Mesmo threshold do drag do webphone (webphone-sticky.tsx) — distingue
// clique de arrasto.
const DRAG_THRESHOLD_PX = 5;

function fmtElapsed(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  if (isNaN(start)) return "0:00";
  const sec = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/**
 * Indicador global de pausa do atendente. Enquanto em pausa mostra um overlay
 * central (que pode ser minimizado pra uma faixa no topo). Some ao encerrar.
 * Estado em localStorage (break-state) — sobrevive navegação/reload.
 */
export function BreakBanner() {
  const [brk, setBrk] = useState<ActiveBreakState | null>(null);
  const [, setTick] = useState(0); // força re-render do contador a cada 1s
  const [ending, setEnding] = useState(false);

  // Sincroniza com o localStorage (start/end vindos do seletor de status, ou
  // de outra aba via 'storage').
  useEffect(() => {
    const sync = () => setBrk(getBreakState());
    sync();
    window.addEventListener(BREAK_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(BREAK_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // #102: detecta fim de pausa (incl. cleanup do fork) pelo status que ja vem
  // no payload de presenca, sem refazer GET /chat/me/availability.
  useEffect(() => {
    const onMyAvailability = (e: Event) => {
      const av = (e as CustomEvent<{ availability: string }>).detail?.availability;
      if (av && av !== "on_break") clearBreakState();
    };
    window.addEventListener(MY_AVAILABILITY_EVENT, onMyAvailability);
    return () => window.removeEventListener(MY_AVAILABILITY_EVENT, onMyAvailability);
  }, []);

  // Tick do contador
  useEffect(() => {
    if (!brk) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [brk]);

  const endBreak = useCallback(async () => {
    if (ending) return;
    setEnding(true);
    try {
      await api.request("/chat/me/breaks/end", { method: "POST" });
      clearBreakState();
      emitMyAvailability("online");
      toast.success("Pausa encerrada.");
    } catch {
      toast.error("Falha ao encerrar pausa.");
    } finally {
      setEnding(false);
    }
  }, [ending]);

  const setMinimized = useCallback(
    (minimized: boolean) => {
      if (!brk) return;
      setBreakState({ ...brk, minimized });
    },
    [brk],
  );

  if (!brk) return null;

  const elapsed = fmtElapsed(brk.startedAt);
  const reason = brk.reason || "Pausa";

  // Faixa minimizada — nasce topo-centro, arrastável livremente pela tela.
  if (brk.minimized) {
    return (
      <MinimizedBreakPill
        reason={reason}
        elapsed={elapsed}
        ending={ending}
        onExpand={() => setMinimized(false)}
        onEnd={endBreak}
      />
    );
  }

  // Overlay central — escurece a tela e centraliza o card.
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
      <div className="w-[340px] rounded-2xl border border-amber-500/30 bg-card p-6 text-center shadow-2xl">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15">
          <Coffee className="h-8 w-8 text-amber-500" />
        </div>
        <h2 className="text-lg font-semibold">Você está em pausa</h2>
        <div className="mt-1 inline-flex items-center rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
          {reason}
        </div>
        <div className="my-5 text-4xl font-mono font-bold tabular-nums tracking-tight">
          {elapsed}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={endBreak}
            disabled={ending}
            className="w-full rounded-lg bg-amber-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-60"
          >
            {ending ? "Encerrando…" : "Encerrar pausa"}
          </button>
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-sm text-muted-foreground transition-colors hover:bg-muted"
          >
            <Minimize2 className="h-4 w-4" />
            Minimizar
          </button>
        </div>
      </div>
    </div>
  );
}

interface MinimizedBreakPillProps {
  reason: string;
  elapsed: string;
  ending: boolean;
  onExpand: () => void;
  onEnd: () => void;
}

/**
 * Pill arrastável — mesmo padrão de drag do WebphoneBubble (Pointer Events +
 * threshold + clamp na viewport), adaptado pra ancoragem top/left porque a
 * posição padrão é centralizada via CSS (não um canto fixo em px como o
 * webphone). Sem persistência: cada pausa nova volta pro topo-centro.
 */
function MinimizedBreakPill({ reason, elapsed, ending, onExpand, onEnd }: MinimizedBreakPillProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef<{
    startX: number;
    startY: number;
    startPos: { top: number; left: number };
    width: number;
    height: number;
    pointerId: number;
  } | null>(null);
  const movedRef = useRef(false);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = draggingRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!movedRef.current && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
      movedRef.current = true;
    }
    const maxLeft = Math.max(0, window.innerWidth - d.width);
    const maxTop = Math.max(0, window.innerHeight - d.height);
    setPos({
      top: Math.min(Math.max(0, d.startPos.top + dy), maxTop),
      left: Math.min(Math.max(0, d.startPos.left + dx), maxLeft),
    });
  }, []);

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const d = draggingRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      draggingRef.current = null;
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    },
    [onPointerMove],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // só botão esquerdo / toque primário
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // No primeiro arrasto converte de "centralizado por CSS" pra "top/left em
    // px" usando a posição já renderizada — sem isso o pill saltaria.
    const startPos = pos ?? { top: rect.top, left: rect.left };
    movedRef.current = false;
    draggingRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPos,
      width: rect.width,
      height: rect.height,
      pointerId: e.pointerId,
    };
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  // O pointerdown do arrasto começa em cima dos botões (Expandir/Encerrar)
  // também — como a posição segue o cursor 1:1, o ponteiro solta exatamente
  // sobre o botão e o click nativo dispara mesmo após um arrasto. Por isso,
  // igual ao WebphoneBubble, ignora o click quando houve movimento acima do
  // threshold.
  const guardedClick = (fn: () => void) => () => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    fn();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-[60] cursor-grab select-none active:cursor-grabbing",
        pos === null && "top-3 left-1/2 -translate-x-1/2",
      )}
      style={{ ...(pos ? { top: pos.top, left: pos.left } : undefined), touchAction: "none" }}
      onPointerDown={onPointerDown}
    >
      <div className="flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/95 px-3 py-1.5 text-white shadow-lg">
        <Pause className="h-3.5 w-3.5 shrink-0" fill="currentColor" />
        <span className="text-xs font-medium">Em pausa</span>
        <span className="text-xs opacity-90">· {reason}</span>
        <span className="text-xs font-mono tabular-nums">· {elapsed}</span>
        <button
          type="button"
          onClick={guardedClick(onExpand)}
          className="ml-1 rounded p-0.5 hover:bg-white/20"
          title="Expandir"
          aria-label="Expandir"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={guardedClick(onEnd)}
          disabled={ending}
          className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold hover:bg-white/30 disabled:opacity-60"
        >
          {ending ? "..." : "Encerrar"}
        </button>
      </div>
    </div>
  );
}
